import React, { Suspense } from 'react'
import ArticleBody from './ArticleBody'
import BlogBottomCTA from '../../page-components/BlogBottomCTA'
import ScrollToTop from '../../components/ScrollToTop'
import clsx from 'clsx'
import spaceStyles from '../../styles/space.module.scss'
import styles from './styles.module.scss'
import Nav from '../../page-components/Navigation'
import Banner from '../../components/Banner'
import TestMetadata from '../../components/Test-Metadata'
import ScrollToTopOnMount from '../../util/ScrollToTopOnMount'
import BreadcrumbAdapter from './Breadcrumbs/BreadcrumbAdapter'
import PlaceholderFactory from '../../page-components/PlaceholderFactory'
import Section from '../../components/Section/Section'
import { BooleanField, ImageField, Item } from '../../types/SitecoreAdapter'
import Hero, { HeroProps } from '../../page-components/Hero'
import { Placeholder, RouteData } from '@sitecore-jss/sitecore-jss-react'
import HeroAppStartSwitch from '../../quotes/HeroAppStartSwitch'
import { get } from 'lodash-es'


type BlogPageProps = {
  route: {
    fields: {

      hero: Item<HeroProps>
      tpWidgetGlobalSettings: {
        fields: {
          enableTrustPilotHeroWidget: BooleanField
        }
      }
      thumbnail: ImageField
    }
  } & RouteData
}

const BlogPage = ({ route }: BlogPageProps) =>{
  const heroProps = getHeroProps({route});

  return (
    <>
      <Nav />
      <Banner />
      <div className='copy blog-page'>
        <Hero {...heroProps} tag='div'>
          <HeroAppStartSwitch quoteButtonTextOverride={heroProps.quoteButtonText?.value}
            darkBg={get(route.fields.hero, 'fields.isDark.value', true)}
            />
        </Hero>
        <Placeholder name="Hero-Placeholder" rendering={route} /> 
      </div>
      <TestMetadata pageType="BlogPost" appStartType="Typeahead" />
      <BreadcrumbAdapter />
        <div className="copy-article blog-article">
            <div className={clsx('block', styles.article)}>
              <ArticleBody />
            </div>
            <Placeholder name="Blog-After-Placeholder" rendering={route} />
            <Section backgroundColor="#FFFFFF">
              <BlogBottomCTA />
            </Section>
        </div>
      <Suspense>
        <ScrollToTop />
        <ScrollToTopOnMount />
      </Suspense>
    </>
  )
}

export default BlogPage

function getHeroProps({ route }: BlogPageProps): HeroProps {
  const { hero, thumbnail, tpWidgetGlobalSettings } = route.fields;
  const { enableTrustPilotHeroWidget } = tpWidgetGlobalSettings.fields;

  const totalPlaceholders = 6;
  let imageIndex = ((route.displayName?.length ?? 1) % totalPlaceholders) + 1;
  if (imageIndex < 1){
    imageIndex = 1;
  }
  const imagePath = `/-/media/placeholders/heros/${imageIndex}/`

  // Create a default image from the thumbnail data.
  const defaultDesktop: ImageField = {
    value: {
      src: imagePath + "desktop",
      alt: "A hero image",
      title: "",
    },
  };

  const defaultLaptop: ImageField = {
    value: {
      src: imagePath + "laptop",
      alt: "A hero image",
      title: "",
    },
  };

  const defaultTablet: ImageField = {
    value: {
      src: imagePath + "tablet",
      alt: "A hero image",
      title: "",
    },
  };

  const defaultMobiles: ImageField = {
    value: {
      src: imagePath + "mobile",
      alt: "A hero image",
      title: "",
    },
  };

  // If no hero exists, return a default hero that satisfies HeroProps.
  if (!hero || !hero.fields) {
    console.log("[Hero] - Default hero used.");
    const defaultHero: HeroProps = {
      heading: { value: "The #1 digital agency for small business insurance" },
      descriptor: { value: "" },
      backgroundImage: defaultLaptop,
      // Include the responsive image fields with default image
      desktopImage: defaultDesktop,
      mobileImage: defaultMobiles,
      tabletImage: defaultTablet,
      laptopImage: defaultLaptop,
      isDark: { value: true },
      quoteButtonText: { value: "" },
      showTrustPilotWidget: { value: true },
      applyDefaultImgSizing: true,
    };
    return defaultHero;
  }

  // Helper function to determine if an image field is "empty"
  const isImageEmpty = (imageField: ImageField): boolean => {
    return !imageField?.value?.src;
  };

  // If the hero datasource exists but its images are missing, assign the default as fallback.
  if (
    isImageEmpty(hero.fields.desktopImage) &&
    isImageEmpty(hero.fields.backgroundImage) &&
    isImageEmpty(hero.fields.mobileImage) &&
    isImageEmpty(hero.fields.tabletImage) &&
    isImageEmpty(hero.fields.laptopImage)
  ) {
    hero.fields.desktopImage = defaultDesktop;
    hero.fields.backgroundImage = defaultLaptop;
    hero.fields.mobileImage = defaultMobiles;
    hero.fields.tabletImage = defaultTablet;
    hero.fields.laptopImage = defaultLaptop;
    console.log("[Hero] - No images found, using default.")

  }

  // If the hero datasource exists but is missing a heading, add a default.
  if (!hero.fields.heading || hero.fields.heading.value === "") {
    hero.fields.heading.value = "#1 digital agency for small business insurance";
    console.log("[Hero] - No heading found, using default.")
  }

  // Build the heroProps with the merged fields and additional properties.
  const heroProps = {
    ...hero.fields,
    applyDefaultImgSizing: true,
    enableTrustPilotHeroWidget
  };

  return heroProps;
}
